import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { formatCurrency as fmtMoney, formatUnitPrice as fmtUnit } from "../lib/format";

/**
 * Cross-Supplier Benchmarking.
 *
 * Products bought from more than one supplier (matched on normalized SKU),
 * showing what we pay each supplier from INVOICED prices (not price files —
 * coverage is patchy). The description-based confidence % flags whether the
 * shared SKU really is the same product across suppliers, so a buyer can trust
 * the high-confidence rows and sanity-check the low ones before acting.
 */

const ROWS_STEP = 50;

function ConfidenceBadge({ value }) {
  if (value == null) {
    return <span title="Only one supplier had a usable description — can't verify" style={{ fontSize: "0.75rem", color: "var(--octopus-voltage)", opacity: 0.6 }}>Unverified</span>;
  }
  const band = value >= 70 ? { fg: "#86efac", bg: "rgba(34,197,94,0.15)", bd: "#22c55e", label: "High" }
    : value >= 40 ? { fg: "#FFD84F", bg: "rgba(255,216,79,0.15)", bd: "#FFD84F", label: "Medium" }
    : { fg: "#fda4af", bg: "rgba(244,63,94,0.15)", bd: "#f43f5e", label: "Low" };
  return (
    <span title="How closely the suppliers' descriptions agree — a same-product check on the shared SKU"
      style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700, background: band.bg, color: band.fg, border: `1px solid ${band.bd}` }}>
      {value}% {band.label}
    </span>
  );
}

const Benchmark = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [minConfidence, setMinConfidence] = useState(70); // default: high-confidence only
  const [period, setPeriod] = useState(6);                // months: 3 / 6 / 12
  const [visibleCount, setVisibleCount] = useState(ROWS_STEP);
  const [expanded, setExpanded] = useState({});

  usePageTitle("Benchmarking");

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await apiGet(`/dashboard/cross-supplier-benchmark?months=${period}`, { timeout: 60000 });
      setData(res || { groups: [], summary: {} });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    let g = data?.groups || [];
    const q = search.trim().toLowerCase();
    if (q) g = g.filter(x => (x.label || "").toLowerCase().includes(q) || (x.suppliers || []).some(s => s.supplier.toLowerCase().includes(q)));
    if (minConfidence > 0) g = g.filter(x => (x.confidence ?? -1) >= minConfidence);
    return g;
  }, [data, search, minConfidence]);

  const filteredSaving = useMemo(
    () => groups.reduce((sum, g) => sum + (g.potential_saving || 0), 0),
    [groups]
  );

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div data-testid="benchmark-page">
      <div className="page-header">
        <h1 className="page-title">Cross-Supplier Benchmarking</h1>
        <p className="page-subtitle">
          Products you buy from more than one supplier — what you currently pay vs the cheapest, and where you could save.
          Prices are from invoices; the confidence % checks the shared part number really is the same product.
          Transport/freight exclusion is configurable in Settings.
        </p>
      </div>

      {loading ? (
        <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)" }}>
          <Loader2 size={24} style={{ animation: "spin 1s linear infinite", marginBottom: "0.5rem" }} />
          <p>Comparing prices across suppliers…</p>
        </div>
      ) : error ? (
        <div className="card" style={{ padding: "2rem", textAlign: "center" }} data-testid="benchmark-error">
          <p style={{ color: "var(--octopus-pink)", marginBottom: "0.5rem" }}>Failed to load benchmarking</p>
          <button onClick={load} style={{ border: "none", background: "transparent", color: "var(--octopus-pink)", cursor: "pointer", fontWeight: 600 }}>Retry</button>
        </div>
      ) : !(data?.groups || []).length ? (
        <div className="card" style={{ padding: "3rem", textAlign: "center", color: "var(--octopus-voltage)", opacity: 0.6 }}>
          No products found that you buy from more than one supplier (matched on part number) in the last 12 months.
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
            <div className="stat-card" style={{ padding: "1.25rem" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--octopus-voltage)", marginBottom: "0.4rem" }}>Multi-supplier products</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--octopus-ice)" }}>{(data.summary?.groups || 0).toLocaleString()}</div>
            </div>
            <div className="stat-card" style={{ padding: "1.25rem" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "#18F0B8", marginBottom: "0.4rem" }}>Potential annual saving</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--octopus-ice)" }} data-testid="benchmark-total-saving">{fmtMoney(data.summary?.total_potential_saving)}</div>
            </div>
            <div className="stat-card" style={{ padding: "1.25rem" }}>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--octopus-voltage)", marginBottom: "0.4rem" }}>Showing (filtered)</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--octopus-ice)" }}>{groups.length.toLocaleString()} · {fmtMoney(filteredSaving)}</div>
            </div>
          </div>

          {/* Controls */}
          <div className="card" style={{ marginBottom: "1rem", display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(ROWS_STEP); }}
              placeholder="Search part code or supplier…"
              data-testid="benchmark-search"
              className="form-input"
              style={{ flex: 1, minWidth: "220px" }}
            />
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)" }}>Period</span>
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  data-testid={`benchmark-period-${m}`}
                  onClick={() => { setPeriod(m); setVisibleCount(ROWS_STEP); }}
                  title="Window the prices and 'current' supplier are measured over. Potential saving is always shown per year."
                  style={{
                    padding: "0.35rem 0.75rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
                    border: "1px solid rgba(96,240,248,0.2)",
                    background: period === m ? "rgba(96,240,248,0.15)" : "transparent",
                    color: period === m ? "var(--octopus-ice)" : "var(--octopus-voltage)",
                  }}
                >
                  {m}m
                </button>
              ))}
            </div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)" }}>Min confidence</span>
              {[0, 40, 70].map((c) => (
                <button
                  key={c}
                  data-testid={`benchmark-conf-${c}`}
                  onClick={() => { setMinConfidence(c); setVisibleCount(ROWS_STEP); }}
                  style={{
                    padding: "0.35rem 0.75rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 600,
                    border: "1px solid rgba(96,240,248,0.2)",
                    background: minConfidence === c ? "rgba(96,240,248,0.15)" : "transparent",
                    color: minConfidence === c ? "var(--octopus-ice)" : "var(--octopus-voltage)",
                  }}
                >
                  {c === 0 ? "Any" : c === 40 ? "Medium+" : "High"}
                </button>
              ))}
            </div>
          </div>

          {/* Opportunities table */}
          <div className="card" data-testid="benchmark-table">
            <div style={{ overflowX: "auto" }}>
              <table className="data-table" style={{ fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={{ width: "28px" }}></th>
                    <th>Part code</th>
                    <th style={{ textAlign: "center" }}>Confidence</th>
                    <th style={{ textAlign: "center" }}>Suppliers</th>
                    <th title="The supplier you order this from most often in the selected period — i.e. what you're currently doing">Current</th>
                    <th style={{ textAlign: "right" }} title="Your current supplier's invoiced unit price">Current price</th>
                    <th>Cheapest</th>
                    <th style={{ textAlign: "right" }}>Cheapest price</th>
                    <th style={{ textAlign: "right" }} title="Gap between the most and least expensive supplier for this part: (highest − lowest) ÷ lowest. Bigger spread = bigger opportunity.">Spread ⓘ</th>
                    <th style={{ textAlign: "right" }} title="Estimated saving over a full year if this part's volume moved to the cheapest supplier. Always annualised, regardless of the period selected.">Potential saving / yr</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.slice(0, visibleCount).map((g) => (
                    <Fragment key={g.key}>
                      <tr data-testid={`benchmark-row-${g.key}`} onClick={() => toggle(g.key)} style={{ cursor: "pointer" }}>
                        <td>{expanded[g.key] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td style={{ fontFamily: "monospace", fontWeight: 600, color: "var(--octopus-ice)" }}>{g.label}</td>
                        <td style={{ textAlign: "center" }}><ConfidenceBadge value={g.confidence} /></td>
                        <td style={{ textAlign: "center" }}>{g.supplier_count}</td>
                        <td style={{ fontWeight: 600, color: g.current_is_cheapest ? "#86efac" : "var(--octopus-ice)" }}>
                          {g.current}{g.current_is_cheapest && " ✓"}
                        </td>
                        <td style={{ textAlign: "right", fontFamily: "monospace", color: g.current_is_cheapest ? "#86efac" : "#fda4af" }}>{fmtUnit(g.current_price)}</td>
                        <td style={{ color: "#86efac", fontWeight: 600 }}>{g.cheapest}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmtUnit(g.cheapest_price)}</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace", color: g.spread_pct > 0 ? "#fda4af" : "var(--octopus-voltage)" }}>{g.spread_pct}%</td>
                        <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 700, color: "#18F0B8" }}>{fmtMoney(g.potential_saving)}</td>
                      </tr>
                      {expanded[g.key] && (
                        <tr>
                          <td></td>
                          <td colSpan={9} style={{ padding: "0.5rem 0.75rem 1rem" }}>
                            <table className="data-table" style={{ fontSize: "0.8rem", marginBottom: 0, background: "rgba(10,18,42,0.4)" }}>
                              <thead>
                                <tr>
                                  <th>Supplier</th>
                                  <th>Their description</th>
                                  <th style={{ textAlign: "right" }}>Unit price</th>
                                  <th style={{ textAlign: "right" }}>vs cheapest</th>
                                  <th style={{ textAlign: "right" }}>Qty ({period}m)</th>
                                  <th style={{ textAlign: "right" }}>Spend ({period}m)</th>
                                  <th>Last bought</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.suppliers.map((s) => (
                                  <tr key={s.supplier}>
                                    <td style={{ fontWeight: 600, color: s.supplier === g.cheapest ? "#86efac" : "var(--octopus-ice)" }}>
                                      {s.supplier}{s.supplier === g.cheapest && " ✓"}
                                    </td>
                                    <td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.description}>{s.description || "—"}</td>
                                    <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmtUnit(s.price)}</td>
                                    <td style={{ textAlign: "right", fontFamily: "monospace", color: s.pct_vs_cheapest > 0 ? "#fda4af" : "#86efac" }}>
                                      {s.pct_vs_cheapest > 0 ? `+${s.pct_vs_cheapest}%` : "cheapest"}
                                    </td>
                                    <td style={{ textAlign: "right", fontFamily: "monospace" }}>{s.qty?.toLocaleString()}</td>
                                    <td style={{ textAlign: "right", fontFamily: "monospace" }}>{fmtMoney(s.spend)}</td>
                                    <td style={{ whiteSpace: "nowrap" }}>{s.last_date || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {groups.length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign: "center", padding: "2rem", opacity: 0.6 }}>No products match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {groups.length > visibleCount && (
              <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                <button onClick={() => setVisibleCount(c => c + ROWS_STEP)} data-testid="benchmark-show-more"
                  style={{ padding: "0.4rem 1rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.8rem", background: "rgba(96,240,248,0.08)", color: "var(--octopus-ice)", border: "1px solid rgba(96,240,248,0.25)", fontWeight: 600 }}>
                  Show {Math.min(ROWS_STEP, groups.length - visibleCount)} more ({(groups.length - visibleCount).toLocaleString()} remaining)
                </button>
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: "1.5rem", fontSize: "0.8rem", color: "var(--octopus-voltage)", opacity: 0.85, lineHeight: 1.6 }}>
            <strong style={{ color: "var(--octopus-ice)" }}>How this works:</strong> products are matched across suppliers on their part
            number (normalised for case, spaces and dashes). Prices are the volume-weighted unit price you were actually invoiced over the
            selected period — price files aren't used, since their coverage is patchy. <strong>Current</strong> is the supplier you order
            this part from most often in the period (what you're doing today); <strong>Cheapest</strong> is the lowest-priced supplier.
            The <strong>confidence %</strong> compares the suppliers' own descriptions of the part: high means they clearly describe the same
            thing; low means the shared code may be a coincidence, so check before switching. <strong>Potential saving</strong> is always
            shown per year (annualised from the selected period) and assumes the part's volume moved to the cheapest supplier — a ceiling,
            not a forecast (MOQs, lead times and quality still apply).
          </div>
        </>
      )}
    </div>
  );
};

export default Benchmark;
